<template>
    <b-modal id="marking-modal" hide-footer centered size="xl"
             hide-header
             :no-close-on-backdrop="true"
             @hide="onHide"
             ref="markingModal">
        <div v-if="marking" class="row marking-modal-inner">
            <button class="modal-close-btn" @click="cancel" aria-label="Close">&#10005;</button>
            <div class="col-3 menu-border-right">
                <div class="text-danger fs-4 mt-1 mb-3 font-weight-bold">{{ modalTitle }}</div>
                <ul class="menu">
                    <li :class="{ active: isActive('active') }" @click="setTab('active')">
                        <span>Active Information</span>
                    </li>
                    <label v-if="isActive('active') && showChangeLogCheck"
                           class="checkbox-label pl-5 d-flex align-items-center mb-0">
                        Mobile added info
                        <input type="checkbox" v-model="showActivePending" @click.stop>
                        <span class="custom-checkbox"></span>
                    </label>
                    <template v-if="!viewOnly">
                    <div class="border-top border-light my-2"></div>
                    <li class="parent"
                        :class="{ active: isParentActive(['content', 'selected-pictures', 'pending-pictures', 'trashed-pictures']) }"
                        @click="setTab('content')">
                        Edit
                    </li>
                    <ul class="submenu">
                        <li :class="{ active: isActive('content') }" @click="setTab('content')">Content</li>
                        <label v-if="isActive('content') && showChangeLogCheck"
                               class="checkbox-label pl-5 d-flex align-items-center mb-0">
                            Mobile added info
                            <input type="checkbox" v-model="showEditPending" @click.stop>
                            <span class="custom-checkbox"></span>
                        </label>
                        <li class="parent"
                            :class="{ active: isParentActive(['selected-pictures', 'pending-pictures', 'trashed-pictures']) }"
                            @click="setTab('selected-pictures')">
                            Pictures
                        </li>
                        <ul class="submenu">
                            <li :class="{ active: isActive('selected-pictures') }"
                                @click="setTab('selected-pictures')">Selected Pictures
                            </li>
                            <li :class="{ active: isActive('pending-pictures'), 'menu-disabled': !hasPendingImages }"
                                @click="hasPendingImages && setTab('pending-pictures')">Pending
                                Pictures <DotagBadge :count="pendingImagesCount" size="s"/>
                            </li>
                            <li :class="{ active: isActive('trashed-pictures'), 'menu-disabled': !hasTrashedAttachments }"
                                @click="hasTrashedAttachments && setTab('trashed-pictures')">Trashed Pictures
                            </li>
                        </ul>
                    </ul>
                    <div class="border-top border-light my-2"></div>
                    <li :class="{ active: isActive('changelog') }" @click="setTab('changelog')">Changelog</li>
                    </template>
                </ul>
                <template v-if="!viewOnly">
                <b-button v-if="!marking.deleted_at" @click="deleteMarking" variant="link" class="text-danger">
                    <b-icon icon="trash"></b-icon>
                    Delete marking
                </b-button>

                <b-button v-else @click="undeleteMarking" variant="link" class="text-success">
                    <b-icon icon="box-arrow-in-up-left"></b-icon>
                    Restore marking
                </b-button>
                </template>
            </div>
            <div class="col-9">
                <h5 class="mt-1 mb-3">{{ tabHeadline }}</h5>
                <div class="mb-2">
                    <div v-if="hasHitAttachmentLimits && isParentActive(['selected-pictures', 'pending-pictures', 'trashed-pictures'])">
                        <b-icon icon="info" class="rounded-circle bg-danger text-white"></b-icon>
                        <span> Only 6 active attachments are allowed for a marking.</span>
                    </div>
                    <div v-if="(isActive('active') && showActivePending) || (isActive('content') && showEditPending)"
                         class="d-flex align-items-center justify-content-between">
                        <div>
                            <template v-if="hasMobileChanges">
                                <b-icon icon="circle-fill" class="text-warning"></b-icon>
                                <span>Marked rows have changes from the mobile app.</span>
                            </template>
                        </div>
                        <label class="sort-label">
                            Sort:
                            <select v-model="mobileViewMode" class="mobile-view-mode-select">
                                <option value="aggregate">all syncs</option>
                                <option value="per-user">per user</option>
                            </select>
                        </label>
                    </div>
                </div>

                <div class="marking-modal-content p-3">
                    <!-- Active View -->
                    <div class="marking-modal-scroll-wrapper"
                         v-if="marking && this.activeTab =='active'">
                        <MobileHeaderInfo
                            :col="'col-sm-8'"
                            :show="showActivePending"
                            :columns="mobileColumns"/>
                        <div class="row edit-row"
                             v-if="marking.title">
                            <div class="col-4" :class="{ 'has-changes': hasChangeAcrossUsers(marking.title, 'title') }">
                                <p><strong>Headline</strong></p>
                            </div>
                            <div :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">
                                <p>{{ marking.title }}</p>
                            </div>
                            <MobileChangeLogCells field="title" :columns="mobileColumns" :show="showActivePending">
                                <template #default="{ value }"><p>{{ value || '-' }}</p></template>
                            </MobileChangeLogCells>
                        </div>

                        <template v-if="marking.type === 'checkbox'">
                            <div class="row edit-row">
                                <div class="col-sm-4" :class="{ 'has-changes': hasChangeAcrossUsers(marking.is_checked, 'is_checked') }">
                                    <p><strong>Status</strong></p>
                                </div>
                                <div :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">
                                    <p class="text-pre-wrap">{{
                                            marking.is_checked ? 'OK' : 'Not OK'
                                        }}</p>
                                </div>
                                <MobileChangeLogCells field="is_checked" :columns="mobileColumns" :show="showActivePending" single-col-class="col-sm-4">
                                    <template #default="{ value }">{{ value == null ? '-' : (value ? 'OK' : 'Not OK') }}</template>
                                </MobileChangeLogCells>

                            </div>
                        </template>

                        <template v-if="marking.tool">
                            <template v-for="index in 20"
                                      v-if="index !== 1 && marking.tool['catalog_' + index]">
                                <div class="row edit-row">
                                    <div class="col-4" :class="{ 'has-changes': hasChangeAcrossUsers(marking['catalog_' + index], 'catalog_' + index) }"><strong>{{ marking.tool['catalog_' + index] }}</strong>
                                    </div>
                                    <div :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">
                                        {{ catalogText(marking, index) }}
                                    </div>

                                    <MobileChangeLogCells :field="'catalog_' + index" :columns="mobileColumns" :show="showActivePending">
                                        <template #default="{ col }">{{ catalogTextFromChangeLog(marking, col ? col.fields : {}, index) }}</template>
                                    </MobileChangeLogCells>
                                </div>
                            </template>

                            <template v-for="index in 20"
                                      v-if="index !== 1 && marking.tool['text_' + index]">
                                <div class="row edit-row">
                                    <div class="col-4" :class="{ 'has-changes': hasChangeAcrossUsers(marking['text_' + index], 'text_' + index) }"><strong>{{ marking.tool['text_' + index] }}</strong>
                                    </div>
                                    <div :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">{{
                                            marking['text_' + index] ? marking['text_' + index] : '-'
                                        }}
                                    </div>

                                    <MobileChangeLogCells :field="'text_' + index" :columns="mobileColumns" :show="showActivePending"/>
                                </div>
                            </template>

                            <template v-if="marking.tool.catalog_1 && marking.tool.catalog_1_values.length > 0">
                                <div class="row edit-row">
                                    <div class="col-4" :class="{ 'has-changes': hasChangeAcrossUsers(marking.catalog_1, 'catalog_1') }"><strong>{{ marking.tool['catalog_1'] }}</strong></div>
                                    <div class="approval-selection"
                                         :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">
                                        <template v-for="color in marking?.tool?.catalog_1_values">
                                            <div class="approval"
                                                 :class="color.value === marking.catalog_1 ? 'active' : ''"
                                                 :style="'background-color: ' + color.value"></div>
                                            <!--                                                 @click="!marking.deleted_at && (marking.catalog_1 = color.value)"-->
                                        </template>
                                    </div>
                                    <MobileChangeLogCells field="catalog_1" :columns="mobileColumns" :show="showActivePending" single-col-class="col-4" extra-class="approval-selection">
                                        <template #default="{ value }">
                                            <div class="approval-selection">
                                                <template v-if="value == null">-</template>
                                                <template v-else>
                                                    <template v-for="color in marking?.tool?.catalog_1_values">
                                                        <div
                                                            class="approval"
                                                            :class="color.value === value ? 'active' : ''"
                                                            :style="'background-color: ' + color.value"
                                                        ></div>
                                                    </template>
                                                </template>
                                            </div>
                                        </template>
                                    </MobileChangeLogCells>
                                </div>
                            </template>
                        </template>

                        <div class="row edit-row">
                            <div class="col-sm-4" :class="{ 'has-changes': hasChangeAcrossUsers(marking.description, 'description') }">
                                <p><strong>Marking description</strong></p>
                            </div>
                            <div :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">
                                <p class="text-pre-wrap">{{ marking.description || '-' }}</p>
                            </div>
                            <MobileChangeLogCells field="description" :columns="mobileColumns" :show="showActivePending">
                                <template #default="{ value }"><p class="text-pre-wrap">{{ value || '-' }}</p></template>
                            </MobileChangeLogCells>
                        </div>

                        <template v-if="marking.created_by">
                            <div class="row edit-row">
                                <div class="col-sm-4">
                                    <strong>Creator</strong>
                                </div>
                                <div :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">
                                    {{ marking.created_by.name }}
                                </div>
                                <MobileChangeLogCells field="_none" :columns="mobileColumns" :show="showActivePending">
                                    <template #default></template>
                                </MobileChangeLogCells>
                            </div>
                        </template>

                        <div class="row edit-row">
                            <div class="col-sm-4">
                                <strong>Created</strong>
                            </div>
                            <div :class="showActivePending ? 'col-sm-4' : 'col-sm-6'">
                                {{ $options.filters.moment(marking.created_at, 'DD.MM.YYYY HH:mm') }}
                            </div>
                            <MobileChangeLogCells field="_none" :columns="mobileColumns" :show="showActivePending">
                                <template #default></template>
                            </MobileChangeLogCells>
                        </div>
                        <!-- Aki sez' nobody uses stupid comments -->
                        <!--                        <Comments :marking="marking" class="mt-3"></Comments>-->
                        <!--                        Pictures Here-->
                        <div class="row">
                            <div class="col-sm-12" v-if="showActivePending">
                                <MarkingAttachmentComparison
                                    :marking="marking"
                                    :all-attachment-change-logs="allAttachmentChangeLogs"
                                    :mobile-logs="changeLogs"
                                    :view-mode="mobileViewMode"
                                    :columns="userChangeLogColumns"
                                    @open-image="openImage">
                                </MarkingAttachmentComparison>
                            </div>
                            <div class="col-sm-12" v-else>
                                <Attachments
                                    class="pt-4"
                                    :marking="marking"
                                    :cardClass="'col-12 col-sm-4'"
                                    :hasHitAttachmentLimits="hasHitAttachmentLimits"
                                    :showControls="false"
                                    @open-image="openImage"
                                    @remove-image="removeAttachment">
                                </Attachments>
                            </div>
                        </div>
                    </div>

                    <!-- Edit Content -->
                    <div class="marking-modal-scroll-wrapper" v-if="marking && this.activeTab =='content'">
                        <MobileHeaderInfo
                            :col="'col-sm-6'"
                            :show="showEditPending"
                            :columns="mobileColumns"/>

                        <div class="row edit-row" v-if="!marking.tool || (marking.tool.show_running_number)">
                            <div class="form-group"
                                 :class="[showEditPending ? 'col-sm-6' : 'col-sm-12', { 'has-changes': hasChangeAcrossUsers(marking.markingIndex, 'index', showEditPending) || hasChangeAcrossUsers(marking.markingIndex, 'markingIndex', showEditPending) }]">
                                <label for="runningNumber">Running Number</label>

                                <div class="d-flex align-items-center gap-2">
                                    <input
                                        v-model="marking.markingIndex"
                                        id="runningNumber"
                                        class="w-25"
                                        :disabled="!isIndexUnlocked"
                                        max="9999"
                                        maxlength="5"
                                        @keyup="onMarkingIndexChange"
                                        placeholder="Marking index"
                                        required
                                        :type="!marking.tool || marking.tool.type === 'firestop' ? 'number' : 'text'"
                                        @change="inputChanged"
                                        @blur="inputBlur"
                                        @focus="inputFocus"
                                        @keypress="inputKeyPress($event)"/>

                                    <span
                                        class="lockIcon"
                                        :class="{ unlocked: isIndexUnlocked }"
                                        @click="toggleIndexUnlock">
                                        <b-icon
                                            :icon="isIndexUnlocked ? 'unlock' : 'lock'"
                                            font-scale="1.2" class=""
                                            variant="light"></b-icon>
                                    </span>
                                </div>
                            </div>

                            <MobileChangeLogCells field="index" :columns="mobileColumns" :show="showEditPending" single-col-class="col-6 pt-3 d-flex align-items-center">
                                <template #default="{ col }">{{ col ? (col.fields.index ?? col.fields.markingIndex ?? '-') : '-' }}</template>
                            </MobileChangeLogCells>

                        </div>

                        <div class="row edit-row"
                             v-if="!marking.tool || marking.tool.type !== 'firestop'">
                            <div :class="[showEditPending ? 'col-sm-6' : 'col-sm-12', { 'has-changes': hasChangeAcrossUsers(marking.title, 'title', showEditPending) }]"
                                 class="form-group">
                                <label for="headline">Marking headline</label>
                                <input id="headline" v-model="marking.title"
                                       :disabled="readOnly ? 'readonly' : false"
                                       :readonly="readOnly ? 'readonly' : false" maxlength="40"
                                       placeholder="Write marking headline here (maximum 40 characters)"
                                       type="text"
                                       @change="inputChanged"
                                       @blur="inputBlur" @focus="inputFocus"
                                       @keypress="inputKeyPress($event)">
                            </div>

                            <MobileChangeLogCells field="title" :columns="mobileColumns" :show="showEditPending" single-col-class="col-6 pt-3 d-flex align-items-center"/>

                        </div>

                        <div v-if="projectTool">
                            <template v-for="index in 20"
                                      v-if="index > 1 && projectTool['catalog_' + index] && projectTool['catalog_' + index + '_values'].length > 0">
                                <div class="row edit-row align-items-stretch">
                                    <div class="form-group"
                                         :class="[showEditPending ? 'col-sm-6' : 'col-sm-12', { 'has-changes': hasChangeAcrossUsers(marking['catalog_' + index], 'catalog_' + index, showEditPending) }]">
                                        <label :for="'catalog_' + index"
                                               v-text="projectTool['catalog_' + index]"></label>
                                        <select
                                            :id="'catalog_' + index"
                                            @change="catalogChange($event, index)"
                                            :value="catalogValue(index)"
                                            :disabled="readOnly ? 'readonly' : false">

                                            <option :value="null"></option>

                                            <option v-for="value in projectTool['catalog_' + index + '_values']"
                                                    v-if="value"
                                                    :value="value.value">
                                                {{ value.text }}
                                            </option>

                                            <option value="other">Other</option>
                                        </select>

                                        <div class="form-group" v-if="catalogValue(index) === 'other'">
                                            <input :id="'catalog_' + index"
                                                   v-text="projectTool['catalog_' + index] + '_other_option'"
                                                   v-model="catalogValues['catalog_' + index]"
                                                   @change="catalogOtherInputValueChange($event, index)"
                                                   :disabled="readOnly ? 'readonly' : false"
                                                   :readonly="readOnly ? 'readonly' : false" maxlength="40"
                                                   placeholder=""
                                                   type="text"
                                                   @blur="inputBlur" @focus="inputFocus"
                                                   @keypress="inputKeyPress($event)">
                                        </div>
                                    </div>

                                    <MobileChangeLogCells :field="'catalog_' + index" :columns="mobileColumns" :show="showEditPending" single-col-class="col-6 pt-3 d-flex align-items-center">
                                        <template #default="{ col }">{{ catalogTextFromChangeLog(marking, col ? col.fields : {}, index) }}</template>
                                    </MobileChangeLogCells>
                                </div>
                            </template>

                            <template v-for="index in 20" v-if="index > 1 && projectTool['text_' + index]">
                                <div class="row edit-row align-items-stretch">
                                    <div class="form-group"
                                         :class="[showEditPending ? 'col-sm-6' : 'col-sm-12', { 'has-changes': hasChangeAcrossUsers(marking['text_' + index], 'text_' + index, showEditPending) }]">
                                        <label :for="'text_input_' + index"
                                               v-text="projectTool['text_' + index]"></label>
                                        <input
                                            :id="'text_input_' + index"
                                            v-model="marking['text_' + index]" :disabled="readOnly ? 'readonly' : false"
                                            :placeholder="projectTool['text_' + index]"
                                            :readonly="readOnly ? 'readonly' : false"
                                            maxlength="40"
                                            type="text"
                                            @change="inputChanged"
                                            @blur="inputBlur" @focus="inputFocus"
                                            @keypress="inputKeyPress($event)">
                                    </div>

                                    <MobileChangeLogCells :field="'text_' + index" :columns="mobileColumns" :show="showEditPending" single-col-class="col-6 pt-3 d-flex align-items-center"/>
                                </div>
                            </template>
                        </div>

                        <div class="row edit-row align-items-stretch">
                            <div class="form-group"
                                 :class="[showEditPending ? 'col-sm-6' : 'col-sm-12', { 'has-changes': hasChangeAcrossUsers(marking.description, 'description', showEditPending) }]">
                                <label for="markingDescription">Marking description</label>
                                <textarea
                                    id="markingDescription"
                                    v-model="marking.description"
                                    :disabled="readOnly ? 'readonly' : false"
                                    :readonly="readOnly ? 'readonly' : false" cols="30" maxlength="1000"
                                    name="description"
                                    placeholder="Write marking description here"
                                    @keydown="inputChanged"
                                    @change="inputChanged"
                                    rows="6" @blur="inputBlur"
                                    @focus="inputFocus"></textarea>
                            </div>
                            <MobileChangeLogCells field="description" :columns="mobileColumns" :show="showEditPending" single-col-class="col-6 pt-3 d-flex align-items-center"/>
                        </div>

                        <div class="row edit-row align-items-stretch"
                             v-if="marking.type === 'checkbox'">
                            <div class="form-group"
                                 :class="[showEditPending ? 'col-sm-6' : 'col-sm-12', { 'has-changes': hasChangeAcrossUsers(marking.is_checked, 'is_checked', showEditPending) }]">
                                <label for="is_checked">
                                    <input
                                        id="is_checked"
                                        v-model="marking.is_checked"
                                        :value="1"
                                        @change="inputChanged"
                                        :disabled="readOnly ? 'readonly' : false"
                                        :readonly="readOnly ? 'readonly' : false" type="radio"/> OK
                                </label>

                                <label for="is_not_checked">
                                    <input
                                        id="is_not_checked"
                                        v-model="marking.is_checked"
                                        :value="0"
                                        @change="inputChanged"
                                        :disabled="readOnly ? 'readonly' : false"
                                        :readonly="readOnly ? 'readonly' : false" type="radio"/> Not OK
                                </label>
                            </div>

                            <MobileChangeLogCells field="is_checked" :columns="mobileColumns" :show="showEditPending" single-col-class="col-6 pt-3 d-flex align-items-center">
                                <template #default="{ value }">{{ value ? 'OK' : 'Not OK' }}</template>
                            </MobileChangeLogCells>
                        </div>

                        <div class="row edit-row align-items-stretch"
                             v-if="projectTool && projectTool.catalog_1">
                            <div :class="[showEditPending ? 'col-sm-6' : 'col-sm-12', { 'has-changes': hasChangeAcrossUsers(marking.catalog_1, 'catalog_1', showEditPending) }]">
                                <div class="approvals">
                                    <div v-for="catalogValue in projectTool.catalog_1_values"
                                         class="row flex my-2 cursor-pointer"
                                         @click="setCatalogValue(1, catalogValue.value)">
                                        <div class="col-1">
                                            <div
                                                :style="{borderStyle: 'solid ', borderColor: catalogValue.value, borderWidth : '2px'}"
                                                class="color">
                                                <svg v-if="marking.catalog_1 === catalogValue.value"
                                                     class="icon-img disabled" height="100%"
                                                     width="100%">
                                                    <use xlink:href="#check-mark"></use>
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="col-9">&nbsp; {{ catalogValue ? catalogValue.text : '' }}</div>
                                        <div class="clearfix"></div>
                                    </div>
                                </div>
                            </div>
                            <MobileChangeLogCells field="catalog_1" :columns="mobileColumns" :show="showEditPending" single-col-class="col-6 pt-3 d-flex align-items-center" extra-class="approval-selection">
                                <template #default="{ value }">
                                    <template v-if="value == null">-</template>
                                    <template v-else>
                                        <template v-for="color in marking?.tool?.catalog_1_values">
                                            <div
                                                class="approval"
                                                :class="color.value === value ? 'active' : ''"
                                                :style="'background-color: ' + color.value"
                                            ></div>
                                        </template>
                                    </template>
                                </template>
                            </MobileChangeLogCells>
                        </div>


                    </div>

                    <!--Selected Pictures-->
                    <div class="marking-modal-scroll-wrapper row"
                         v-if="marking && this.activeTab =='selected-pictures'  || (marking.drawing && marking.drawing.attachment)">
                        <div class="col-sm-12">
                            <div class="row mb-4"
                                 v-if="marking.type === 'draw' && marking.drawing && marking.drawing.attachment">
                                <div class="col-sm-12">
                                    <img class="img-fluid" :src="marking.drawing.attachment"
                                         alt="attachment"/>
                                    <p v-if="marking.drawing.description"
                                       class="mb-0 mt-2 text-pre-wrap"
                                       v-text="marking.drawing.description"></p>
                                </div>
                            </div>
                            <div class="row">
                                <div
                                    v-for="(attachment, g) in marking.attachments.filter(a => !a.is_trashed)"
                                    :key="g"
                                    class="col-12 col-sm-4 mb-4 d-flex justify-content-center">
                                    <div class="card shadow-sm rounded bg-white position-relative attachment-card">
                                        <span
                                            class="badge badge-light d-inline-flex align-items-center position-absolute"
                                            style="z-index: 1; left: 10px; top: 15px;">
                                            <b-icon
                                                :icon="attachment.source === 'saas' ? 'display' : 'phone'"
                                                class="mr-1">
                                            </b-icon>
                                            {{ attachment.source === 'saas' ? 'SaaS' : 'App' }}
                                        </span>

                                        <b-button
                                            class="position-absolute rounded-circle p-0 attachment-circle"
                                            @click="removeAttachment(marking.attachments.indexOf(attachment))"
                                            v-b-tooltip.hover
                                            title="Remove">
                                            <b-icon-trash class="text-white"></b-icon-trash>
                                        </b-button>

                                        <div class="image-wrapper" @click="openImage(attachment)">
                                            <img
                                                class="img-fluid rounded w-100 h-100"
                                                :src="attachment.attachment ? getAttachmentURL(attachment) : attachment.url"
                                                alt="attachment"
                                                style="object-fit: cover;"
                                            />

                                            <div class="image-overlay">
                                                <b-icon-zoom-in class="overlay-icon"></b-icon-zoom-in>
                                            </div>
                                        </div>

                                        <p v-if="attachment.description"
                                           class="mb-0 text-muted text-center w-100 attachment-description"
                                        >{{ attachment.description }}</p>
                                        <div class="attachment-flags">
                                            <label class="checkbox-label d-flex align-items-center mb-0">
                                                Before
                                                <input type="checkbox" v-model="attachment.is_before" @change="onAttachmentFlagChange(attachment, 'is_before')">
                                                <span class="custom-checkbox"></span>
                                            </label>
                                            <label class="checkbox-label d-flex align-items-center mb-0">
                                                After
                                                <input type="checkbox" v-model="attachment.is_after" @change="onAttachmentFlagChange(attachment, 'is_after')">
                                                <span class="custom-checkbox"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="!hasHitAttachmentLimits"
                                     class="col-12 col-sm-4 mb-4 max-h text-center">
                                    <div class="card shadow-sm rounded-4 p-1 upload-card"
                                         @click="$refs.pictureInput.click()">
                                        <div class="upload-placeholder">
                                            <b-icon-card-image class="mb-2 h-50 w-50"/>
                                            <div>Upload picture</div>
                                        </div>

                                        <input
                                            ref="pictureInput"
                                            accept="image/*"
                                            type="file"
                                            style="display: none;"
                                            @change="uploadAttachment"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!--Pending Pictures-->
                    <div class="marking-modal-scroll-wrapper" v-if="activeTab === 'pending-pictures'">
                        <ChangeLogAttachments
                            :changeLogs="attachmentChangeLogs"
                            :marking="marking"
                            :restoring-ids="restoringAttachmentIds"
                            @open-image="openImage"
                            @restore-attachment="addChangeLogAttachment">
                        </ChangeLogAttachments>
                    </div>
                    <!--Trashed Pictures-->
                    <div class="marking-modal-scroll-wrapper row"
                         v-if="marking && this.activeTab =='trashed-pictures' && marking.attachments && marking.attachments.length || (marking.drawing && marking.drawing.attachment)">
                        <div class="col-sm-12">
                            <div class="row mb-4"
                                 v-if="marking.type === 'draw' && marking.drawing && marking.drawing.attachment">
                                <div class="col-sm-12">
                                    <img class="img-fluid" :src="marking.drawing.attachment"
                                         alt="attachment"/>
                                    <p v-if="marking.drawing.description"
                                       class="mb-0 mt-2 text-pre-wrap"
                                       v-text="marking.drawing.description"></p>
                                </div>
                            </div>
                            <div class="row" v-if="marking.attachments && marking.attachments.length">
                                <div
                                    v-for="(attachment, g) in marking.attachments.filter(a => a.is_trashed)"
                                    :key="g"
                                    class="col-12 col-sm-4 mb-4 max-h text-center">
                                    <div class="card shadow-sm rounded bg-white position-relative attachment-card">
                                        <span
                                            class="badge badge-light d-inline-flex align-items-center position-absolute"
                                            style="z-index: 1; left: 10px; top: 15px;">
                                            <b-icon
                                                :icon="attachment.source === 'saas' ? 'display' : 'phone'"
                                                class="mr-1">
                                            </b-icon>
                                            {{ attachment.source === 'saas' ? 'SaaS' : 'App' }}
                                        </span>
                                        <b-button
                                            class="primary position-absolute rounded-circle p-0 attachment-circle"
                                            :class="{ 'btn-disabled': hasHitAttachmentLimits }"
                                            @click="!hasHitAttachmentLimits && restoreAttachment(marking.attachments.indexOf(attachment))"
                                            v-b-tooltip.hover
                                            title="Restore">
                                            <b-icon-arrow-up-left-circle></b-icon-arrow-up-left-circle>
                                        </b-button>

                                        <div class="image-wrapper" @click="openImage(attachment)">
                                            <img
                                                class="img-fluid rounded w-100 h-100"
                                                :src="attachment.attachment ? getAttachmentURL(attachment) : attachment.url"
                                                alt="attachment"
                                                style="object-fit: cover;"
                                            />

                                            <div class="image-overlay">
                                                <b-icon-zoom-in class="overlay-icon"></b-icon-zoom-in>
                                            </div>
                                        </div>

                                        <p v-if="attachment.description"
                                           class="mb-0 text-muted text-center position-absolute w-100 attachment-description"
                                           v-text="attachment.description"
                                        ></p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!--Change Logs-->
                    <div class="marking-modal-scroll-wrapper" v-if="activeTab === 'changelog'">
                        <ChangeLogHistory
                            :marking ="marking"
                            :changeLogs="changeLogs"
                            :show-active-pending="showActivePending"/>
                    </div>
                </div>

                <div class="align-right mt-3" v-if="viewOnly">
                    <b-button @click="cancel" variant="secondary" >Close</b-button>
                </div>
                <div class="align-right mt-3" v-else>
                    <b-button @click="cancel" variant="secondary" >Cancel</b-button>
                    <span v-if="savedRecently && !unsavedChanges" class="ml-2 text-success d-inline-flex align-items-center">Changes saved!</span>
                    <b-button v-else @click="save" variant="primary" :disabled="!unsavedChanges" >
                        Save Changes
                    </b-button>

                </div>
            </div>
        </div>

        <b-modal v-model="showExitConfirm" centered hide-header :no-close-on-backdrop="true">
            <p class="mb-0">You have unsaved changes. What do you want to do?</p>
            <template #modal-footer>
                <b-button variant="outline-secondary" @click="exitWithoutSaving">Exit without saving</b-button>
                <b-button variant="secondary" @click="showExitConfirm = false">Stay</b-button>
                <b-button variant="danger" @click="saveAndExit">Save and Exit</b-button>
            </template>
        </b-modal>
    </b-modal>
</template>

<script>
import Comments from "@mobile/components/edit/Comments";
import ChangeLogAttachments from "./ChangeLogAttachments.vue";
import ChangeLogHistory from "./ChangeLogHistory.vue";
import Attachments from "./Attachments.vue"
import MobileHeaderInfo from "./MobileHeaderInfo.vue";
import MobileChangeLogCells from "./MobileChangeLogCells.vue";
import MarkingAttachmentComparison from "./MarkingAttachmentComparison.vue";
import DotagBadge from "../../common/DotagBadge.vue";
import axios from "axios";
import {MAX_HEIGHT, MAX_WIDTH} from "@mobile/utils/constants";
import {fileUtils} from "@mobile/utils/file_utils";

export default {
    components: {Comments, ChangeLogAttachments, ChangeLogHistory, Attachments, MobileHeaderInfo, MobileChangeLogCells, MarkingAttachmentComparison, DotagBadge},
    props: {
        showModal: false,
        marking: {
            type: Object,
            default: null
        },
        projectTools: {
            type: Array,
            default: () => []
        },
        // View-only roles (no site_monitoring.edit): Active Information only —
        // no edit tabs, changelog, mobile added info, delete/restore or save.
        viewOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            restoringAttachmentIds: [],
            showActivePending: false,
            showEditPending: false,
            showContentPending: false,
            activeTab: 'active',
            isIndexUnlocked: false,
            readOnly: false,
            changeLogs: [],
            attachmentChangeLogs: [],
            allAttachmentChangeLogs: [],
            catalogValues: null,
            userChangeLogColumns: [],
            aggregateChangeLogColumn: null,
            mobileViewMode: 'per-user',
            unsavedChanges: false,
            savedRecently: false,
            showExitConfirm: false,
        }
    },
    computed: {
        showChangeLogCheck() {
            return !this.viewOnly;
        },

        projectTool() {
            if (this.marking.project_marking_tool_id) {

                if (this.projectTools) {
                    const tool = this.projectTools.find((tool) => {
                        return tool.id === this.marking.project_marking_tool_id;
                    });
                    if (tool) {
                        this.marking.tool = tool;
                        return tool;
                    }
                }

                if (this.marking.tool) {
                    return this.marking.tool;
                }
            }
        },
        modalTitle() {
            if (!this.marking) return '';
            let label = `${this.marking.markingPrefix}_${this.marking.markingIndex}`;
            if (this.marking.created_by?.unique_id) label += `:${this.marking.created_by.unique_id}`;
            return this.marking.title ? `${label} - ${this.marking.title}` : label;
        },
        tabHeadline() {
            const headlines = {
                active: 'Active Information',
                content: 'Edit Marking Information',
                'selected-pictures': 'Selected Pictures',
                'pending-pictures': 'Pending Pictures',
                'trashed-pictures': 'Trashed Pictures',
                changelog: 'Marking Changelog',
                mobile: 'Mobile View'
            };
            return headlines[this.activeTab] || 'Active Marking';
        },
        hasHitAttachmentLimits() {
            return (this.marking?.attachments || []).filter(a => !a.is_trashed).length > 5;
        },
        hasActiveAttachments() {
            return this.marking?.attachments?.some(a => !a.is_trashed);
        },
        hasTrashedAttachments() {
            return this.marking?.attachments?.some(a => a.is_trashed);
        },
        hasPendingImages() {
            const markingHashes = new Set(
                (this.marking?.attachments || []).map(a => a.hash).filter(Boolean)
            );
            return this.attachmentChangeLogs.some(log =>
                (log.attachments || []).some(img => !markingHashes.has(img.hash))
            );
        },
        pendingImagesCount() {
            const markingHashes = new Set(
                (this.marking?.attachments || []).map(a => a.hash).filter(Boolean)
            );
            let count = 0;
            for (const log of this.attachmentChangeLogs) {
                for (const img of (log.attachments || [])) {
                    if (!markingHashes.has(img.hash)) count++;
                }
            }
            return count;
        },
        hasMobileChanges() {
            if (!this.showChangeLogCheck) return false;
            const keyMap = { index: 'markingIndex', prefix: 'markingPrefix' };
            return this.mobileColumns.some(col =>
                Object.keys(col.fields)
                    .filter(k => k !== 'is_locked')
                    .some(key => {
                        const markingKey = keyMap[key] || key;
                        return this.hasChange(this.marking[markingKey], col.fields[key], true);
                    })
            );
        },
        mobileColumns() {
            if (this.mobileViewMode === 'aggregate') {
                return this.aggregateChangeLogColumn ? [this.aggregateChangeLogColumn] : [];
            }
            return this.userChangeLogColumns;
        },
    },
    watch: {
        unsavedChanges(val) {
            if (val) this.savedRecently = false;
        },
        'marking.updated_at': function (newVal, oldVal) {
            if (newVal && oldVal && newVal !== oldVal) {
                this.fetchChangeLogs();
            }
        },
        showModal: {
            immediate: true,
            handler(val) {
                if (val) {
// Sync marking.tool with the live tool definition so Active Information always shows the fields added later by the tool designer
                    if (this.marking.project_marking_tool_id && this.projectTools) {
                        const liveTool = this.projectTools.find(t => t.id === this.marking.project_marking_tool_id);
                        if (liveTool) this.marking.tool = liveTool;
                    }

                    this.catalogValues = {
                        catalog_1: this.marking['catalog_1'],
                        catalog_2: this.marking['catalog_2'],
                        catalog_3: this.marking['catalog_3'],
                        catalog_4: this.marking['catalog_4'],
                        catalog_5: this.marking['catalog_5'],
                        catalog_6: this.marking['catalog_6'],
                        catalog_7: this.marking['catalog_7'],
                        catalog_8: this.marking['catalog_8'],
                        catalog_9: this.marking['catalog_9'],
                        catalog_10: this.marking['catalog_10'],
                        catalog_11: this.marking['catalog_11'],
                        catalog_12: this.marking['catalog_12'],
                        catalog_13: this.marking['catalog_13'],
                        catalog_14: this.marking['catalog_14'],
                        catalog_15: this.marking['catalog_15'],
                        catalog_16: this.marking['catalog_16'],
                        catalog_17: this.marking['catalog_17'],
                        catalog_18: this.marking['catalog_18'],
                        catalog_19: this.marking['catalog_19'],
                        catalog_20: this.marking['catalog_20'],
                    };


                    this.fetchChangeLogs();
                    this.unsavedChanges = false;
                    this.$nextTick(() => {
                        this.$refs.markingModal?.show();
                    });
                    this.$refs.markingModal?.show();
                }
            }
        },
    },
    methods: {
        toggleIndexUnlock() {
            if (!this.isIndexUnlocked) {
                this.$bvModal.msgBoxConfirm(
                    'The "Running number" will be unlocked. This field is required. Changing the running number can upset the ordering. Are you sure?',
                    {centered: true}
                ).then(ok => {
                    if (ok) {
                        this.isIndexUnlocked = true
                    }
                })
            } else {
                this.isIndexUnlocked = false
            }
        },

        catalogOtherInputValueChange($event, catalogIndex) {
            this.unsavedChanges = true;
            if ($event.target.value !== '') {
                this.marking['catalog_' + catalogIndex] = $event.target.value;
            } else {
                this.marking['catalog_' + catalogIndex] = 'other';
            }
        },
        setCatalogValue(catalogNr, value) {
            this.unsavedChanges = true;
            this.marking['catalog_' + catalogNr] = (this.marking['catalog_' + catalogNr] === value) ? null : value;
            this.$forceUpdate();
        },
        catalogValue(catalogIndex) {
            if (this.marking['catalog_' + catalogIndex] === null || this.marking['catalog_' + catalogIndex] === undefined) {
                return null;
            }

            if (this.projectTool['catalog_' + catalogIndex + '_values'].filter(catalogValue => {
                if (!catalogValue || catalogValue.value === null || catalogValue.value === undefined) {
                    return false;
                }
                return catalogValue.value.toString() === this.marking['catalog_' + catalogIndex]?.toString();
            }).length > 0) {
                return this.marking['catalog_' + catalogIndex];
            }

            return 'other';
        },
        catalogChange($event, catalogIndex) {
            this.unsavedChanges = true;
            this.marking['catalog_' + catalogIndex] = $event.target.value;
        },

        inputKeyPress(event) {
            this.unsavedChanges = true;
            if (event.keyCode === 13) {
                event.preventDefault();
                event.target.blur();
            }
        },
        inputFocus() {
        },

        inputBlur() {
        },
        inputChanged() {
            this.unsavedChanges = true;
        },

        onAttachmentFlagChange(attachment, field) {
            if (attachment[field]) {
                const other = field === 'is_before' ? 'is_after' : 'is_before';
                this.$set(attachment, other, false);
            }
            this.inputChanged();
        },

        onMarkingIndexChange() {
            if (this.projectTool && this.projectTool.type === 'custom') {
                this.marking.markingIndex = this.marking.markingIndex.slice(0, 5);
            } else {
                this.marking.markingIndex = this.marking.markingIndex.replace(/\D/g, '').slice(0, 3);
            }
        },

        openImage(attachment) {
            const attachmentUrl = attachment.attachment ? this.getAttachmentURL(attachment) : attachment.url
            this.$emit('openLightbox', attachmentUrl);
        },

        uploadAttachment() {
            let files = this.$refs.pictureInput.files;

            if (files.length > 0 && /\.(jpe?g|png|gif|svg)$/i.test(files[0].name)) {
                this.resizeImage({file: files[0]}).then((resizedImage) => {
                    let attachment = {
                        description: new Date().toLocaleDateString('en-GB').replace(/\//g, '.'),
                        attachment: resizedImage,
                        source: 'saas',
                    };

                    if (this.preserveAttachmentName) {
                        attachment.name = files[0].name.replace(/\.(jpe?g|png|gif|svg)$/i, '');
                    }
                    this.preserveAttachmentName = false;

                    attachment.is_before = false;
                    attachment.is_after = false;

                    this.marking.attachments.push(attachment);

                    this.$forceUpdate();
                    this.$store.state.isLoading = false;


                    this.$refs.pictureInput.type = 'text';
                    this.$refs.pictureInput.value = '';
                    this.$refs.pictureInput.type = 'file';
                    this.unsavedChanges = true;

                }).catch((err) => {
                    console.error(err);
                });
            }
        },

        resizeImage({file, maxWidth = MAX_WIDTH ?? 2096, maxHeight = MAX_HEIGHT ?? 2096}) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    let {width, height} = img;

                    if (width > maxWidth) {
                        height = (maxWidth / width) * height;
                        width = maxWidth;
                    }
                    if (height > maxHeight) {
                        width = (maxHeight / height) * width;
                        height = maxHeight;
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob(resolve, 'image/jpeg', 0.85);
                };
                img.onerror = reject;
                img.src = URL.createObjectURL(file);
            });
        },


        getAttachmentURL(attachment) {
            if (typeof attachment.attachment === 'string') {
                return attachment.attachment;
            } else {
                return fileUtils.blobToImgSrc(attachment.attachment);
            }
        },

        restoreAttachment(attachmentIndex) {
            const attachment = this.marking.attachments[attachmentIndex];

            if (!attachment) return;
            attachment.is_trashed = false;
            this.unsavedChanges = true;
            this.$forceUpdate();
        },

        removeAttachment(attachmentIndex) {
            const message = 'Removing the attachment will remove it from the report and place it in the trash. You will be able to restore it later. Proceed?';
            this.$bvModal
                .msgBoxConfirm(message, {
                    title: 'Please confirm',
                    okVariant: 'danger',
                    okTitle: 'OK',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                })
                .then(value => {
                    if (value) {
                        const attachment = this.marking.attachments[attachmentIndex];

                        if (!attachment) return;
                        attachment.is_trashed = true;

                        this.deleteAttachmentConfirmIsVisible = false;
                        this.unsavedChanges = true;
                        this.$forceUpdate();
                    }
                })
                .catch(err => {
                    console.error(err);
                });
        },

        addChangeLogAttachment(attachment) {
            if (this.marking.attachments.filter(a => !a.is_trashed).length >= 6) {
                alert('Only 6 active attachments are allowed!');
                return;
            }
            this.restoringAttachmentIds.push(attachment.media_id);
            this.$store.dispatch('APIChangeLogToMarkingAttachment', {
                changeLogMediaID: attachment.media_id,
                markingID: this.marking.id
            })
                .then(response => {
                    this.activeTab = 'selected-pictures';
                    attachment.is_trashed = false;
                    attachment.media_id = response.media_id;
                    this.marking.attachments.push(attachment);

                    // Remove from attachmentChangeLogs so it no longer shows as pending
                    this.attachmentChangeLogs = this.attachmentChangeLogs
                        .map(log => ({
                            ...log,
                            attachments: (log.attachments || []).filter(a => a !== attachment),
                        }))
                        .filter(log => (log.attachments || []).length > 0);

                    this.unsavedChanges = true;
                })
                .catch(error => {
                    console.error('Failed to attach changelog:', error);
                    alert('Failed to attach changelog. Please try again.');
                })
                .finally(() => {
                    this.restoringAttachmentIds = this.restoringAttachmentIds.filter(id => id !== attachment.media_id);
                });
        },

        catalogText(marking, catalogIndex) {
            if (!marking || marking['catalog_' + catalogIndex] === null) {
                return '-';
            }

            const value = marking.tool?.['catalog_' + catalogIndex + '_values']?.find(catalogValue => {
                if (!catalogValue || catalogValue.value === null || catalogValue.value === undefined) {
                    return false;
                }
                return catalogValue.value.toString() === marking['catalog_' + catalogIndex].toString();
            });

            if (value) {
                return value.text;
            }

            return marking['catalog_' + catalogIndex];
        },
        catalogTextFromChangeLog(marking, changeLogMarking, catalogIndex) {
            const value = changeLogMarking['catalog_' + catalogIndex];

            if (value === null || value === undefined) {
                return '-';
            }

            const toolValues = marking?.tool?.['catalog_' + catalogIndex + '_values'];

            if (Array.isArray(toolValues)) {
                const match = toolValues.find(catalogValue => {
                    if (!catalogValue || catalogValue.value === null || catalogValue.value === undefined) {
                        return false;
                    }
                    return catalogValue.value.toString() === value.toString();
                });

                if (match) {
                    return match.text;
                }
            }

            return value;
        },

        async updateMarking(marking, lock = true) {
            if (!marking) {
                marking = this.marking;
            }

            marking.is_locked = lock;

            const token = this.$store.state.token;

            marking = await this.prepareMarkingAttachments(marking);

            axios({
                url: this.$store.getters.apiProjectBase + '/markings/' + marking.id + '/update-marking',
                data: marking,
                method: 'POST',
                headers: {
                    "Authorization": "Bearer " + token
                }
            })
                .then(response => {
                    let newMarking = response.data.data;

                    this.markings.forEach((marking, index) => {
                        if (marking.id === newMarking.id) {
                            this.markings[index] = this.transformMarking(newMarking);

                            this._computedWatchers.activeMarkings.run();
                            this.$forceUpdate();

                            let message = 'The marking was updated successfully.';

                            this.$bvModal.msgBoxOk(message, {
                                title: 'Success',
                                okVariant: 'danger'
                            });
                        }
                    });

                    this.moveMode = 0;
                }).catch(err => {
                this.moveMode = 0;
            });
        },

        transformMarking(marking) {
            marking.attachments = [];
            if (marking.groupped_attachments && marking.groupped_attachments.length > 0) {
                marking.groupped_attachments.forEach(attachments => {
                    if (attachments.length > 0) {
                        attachments.forEach(attachment => {
                            marking.attachments.push(attachment);
                        });
                    }

                });
                delete marking.groupped_attachments;
            }
            return marking;
        },

        async prepareMarkingAttachments(marking) {
            if (!marking || !marking.attachments || marking.attachments.length === 0) {
                return marking;
            }

            // Clone attachments so we never corrupt the live in-memory marking
            const clonedAttachments = marking.attachments.map(a => ({ ...a }));

            for (let i = 0; i < clonedAttachments.length; i++) {
                const attachment = clonedAttachments[i];
                if (attachment.attachment && attachment.attachment instanceof Blob) {
                    attachment.name = 'marking_' + marking.uuid + '_attachment_' + i + '.jpeg';
                    const reader = new FileReader();
                    reader.readAsDataURL(attachment.attachment);
                    await new Promise(resolve => {
                        reader.onload = () => {
                            // strip data:image/...;base64, prefix — backend expects raw base64
                            attachment.attachment = reader.result.split(',')[1];
                            resolve();
                        }
                    });
                }
            }

            return { ...marking, attachments: clonedAttachments };
        },

        hasChange(activeVal, changeLogVal, visible = null) {
            const isVisible = visible !== null ? visible : this.showActivePending;
            if (!isVisible) return false;
            if (changeLogVal === undefined) return false;
            const normalize = v => (v === null || v === undefined || v === '') ? null : v;
            const a = normalize(activeVal);
            const b = normalize(changeLogVal);
            if (a === b) return false;
            if (a !== null && b !== null) return String(a) !== String(b);
            return true;
        },

        // There's only one label cell per row, so its .has-changes marker has to
        // aggregate across every user's column rather than compare against a single
        // merged value.
        hasChangeAcrossUsers(activeVal, field, visible = null) {
            const isVisible = visible !== null ? visible : this.showActivePending;
            if (!isVisible) return false;
            return this.mobileColumns.some(col => this.hasChange(activeVal, col.fields[field], true));
        },

        hasChangeLogs(marking) {
            return marking.has_change_logs;
        },
        setTab(tab) {
            if (this.viewOnly && tab !== 'active') {
                return;
            }

            this.activeTab = tab;
        },
        isActive(tab) {
            return this.activeTab === tab;
        },
        isParentActive(tabs) {
            return tabs.includes(this.activeTab);
        },
        onHide() {
            this.activeTab = 'active';
            this.$emit('close');
        },
        deleteMarking() {
            if (this.viewOnly) {
                return;
            }

            this.$bvModal
                .msgBoxConfirm('Removing the marking will exclude it from Reports. You can always restore deleted markings later.', {
                    title: 'Are you sure?',
                    okVariant: 'danger',
                    okTitle: 'OK',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                })
                .then(ok => {
                    if (!ok) {
                        return;
                    }
                    this.unsavedChanges = false;
                    this.isIndexUnlocked = false;
                    this.showActivePending = false;
                    this.showEditPending = false;
                    this.$emit('delete', this.marking);
                    this.$refs.markingModal.hide();
                })
                .catch(err => {
                    console.error(err);
                });
        },
        undeleteMarking() {
            if (this.viewOnly) {
                return;
            }

            this.unsavedChanges = false;
            this.isIndexUnlocked = false;
            this.$emit('unDelete', this.marking);
            this.showActivePending = false;
            this.showEditPending = false;
            this.$refs.markingModal.hide();
        },
        save() {
            if (this.viewOnly) {
                return;
            }

            if (this.unsavedChanges) {
                this._pendingWasActive = this.showActivePending;
                this.unsavedChanges = false;
                this.savedRecently = true;
                this.isIndexUnlocked = false;
                this.showActivePending = false;
                this.showEditPending = false;
                this.$emit('save', this.marking, false);
                // this.$refs.markingModal.hide();
            }
        },
        cancel() {
            if (this.unsavedChanges && !this.viewOnly) {
                this.showExitConfirm = true;
            } else {
                this.isIndexUnlocked = false;
                this.showActivePending = false;
                this.showEditPending = false;
                this.$refs.markingModal.hide();
            }
        },
        saveAndExit() {
            this.showExitConfirm = false;
            this.isIndexUnlocked = false;
            this.showActivePending = false;
            this.showEditPending = false;
            this.$emit('save', this.marking, false);
            this.$refs.markingModal.hide();
        },
        exitWithoutSaving() {
            this.showExitConfirm = false;
            this.isIndexUnlocked = false;
            this.showActivePending = false;
            this.showEditPending = false;
            this.$refs.markingModal.hide();
        },

        fetchChangeLogs() {
            if (this.viewOnly) {
                return;
            }

            axios.get(`${this.$store.getters.mangerURL}/api/change-logs`, {
                params: {marking_id: this.marking.id},
                headers: {"Authorization": "Bearer " + this.$store.state.token}
            }).then(res => {
                const data = res.data || [];
                const mobileLogs = data.filter(log => log.action?.platform !== 'browser');

                const byUser = new Map();
                for (const log of mobileLogs) {
                    const uid = log.user_id ?? 'unknown';
                    if (!byUser.has(uid)) byUser.set(uid, []);
                    byUser.get(uid).push(log);
                }

                const columns = [];
                for (const [userId, logs] of byUser) {
                    logs.sort((a, b) => (new Date(a.created_at) - new Date(b.created_at)) || (a.id - b.id));
                    const latestModelData = logs[logs.length - 1].model_data || {};
                    const changedKeys = logs.reduce((acc, log) => ({...acc, ...(log.changed_keys || {})}), {});
                    columns.push({
                        userId,
                        userName: logs[logs.length - 1].user_name || 'Unknown user',
                        uniqueId: logs[logs.length - 1].user_unique_id,
                        latestAt: logs[logs.length - 1].created_at,
                        fields: {...latestModelData, ...changedKeys}
                    });
                }
                columns.sort((a, b) => new Date(b.latestAt) - new Date(a.latestAt));

                this.userChangeLogColumns = columns;

                if (mobileLogs.length > 0) {
                    const sorted = mobileLogs.slice().sort((a, b) => (new Date(a.created_at) - new Date(b.created_at)) || (a.id - b.id));
                    const latestModelData = sorted[sorted.length - 1].model_data || {};
                    const changedKeys = sorted.reduce((acc, log) => ({...acc, ...(log.changed_keys || {})}), {});
                    this.aggregateChangeLogColumn = {
                        userId: 'aggregate',
                        userName: 'Mobile added info',
                        latestAt: sorted[sorted.length - 1].created_at,
                        fields: {...latestModelData, ...changedKeys}
                    };
                } else {
                    this.aggregateChangeLogColumn = null;
                }

                if (this._pendingWasActive !== undefined) {
                    this.showActivePending = this._pendingWasActive;
                    this.showEditPending = this._pendingWasActive;
                    this._pendingWasActive = undefined;
                } else if (this.showChangeLogCheck) {
                    this.showActivePending = true;
                    this.showEditPending = true;
                }

                this.changeLogs =  (data || []).slice();
            });


            this.$store.dispatch('APIgetMarkingChangelogAttachments', {
                file_id: this.marking.file_id,
                marking_id: this.marking.id
            }).then(res => {
                // Only this line if we want to display them even when actives are set
                // this.attachmentChangeLogs = res || [];

                // OR
                // Filter out changelog images that already exist in ACTIVE attachments
                // including BOTH active and trashed attachments.
                const changeLogs = res || [];

                this.allAttachmentChangeLogs = changeLogs;

                const allAttachmentHashes = new Set(
                    (this.marking?.attachments || [])
                        .map(a => a.hash)
                        .filter(Boolean)
                );
                this.attachmentChangeLogs = changeLogs
                    .map(log => ({
                        ...log,
                        attachments: (log.attachments || []).filter(img => !allAttachmentHashes.has(img.hash)),
                    }))
                    .filter(log => (log.attachments || []).length > 0);
            });
        },
    }
}
</script>

<style scoped>
.color {
    width: 25px;
    height: 25px;
}

.approval {
    border: dashed #707070 1px;
    color: #A91E23;
    text-align: center;
    width: 100%;
    padding: 0 10px;
}

input.small_input {
    font-size: 10px;
    padding: 5px;
    height: 20px;
}

input.error {
    border: solid 1px #dc2626;
}

::v-deep .edit-row {
    border-bottom: solid 1px #f8f9fa;
}

.edit-row > * {
    padding: 10px 15px;
}

::v-deep .from-mobile {
    background: #ececec;
}

::v-deep .from-mobile-header {
    text-align: center;
    color: #dc2626;
    font-size: 20px;
}
::v-deep .overlay-icon {
    font-size: 3rem;
    color: #fff;
    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
}

.attachment-card {
    display: flex;
    flex-direction: column;
    height: auto;
}

.attachment-card .image-wrapper {
    height: 200px;
    flex-shrink: 0;
}

.attachment-card .attachment-description {
    position: static;
    background: rgba(0, 0, 0, 0.04);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    padding: 3px 8px;
    font-size: 0.8rem;
}

.attachment-flags {
    display: flex;
    justify-content: center;
    gap: 1rem;
    padding: 3px 8px 5px;
    font-size: 0.8rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.lockIcon {
    width: 30px;
    margin-top: 5px;
    margin-left: 4px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-repeat: no-repeat;
    background-position: center;
    background-color: #dc2626;
    border: 1px solid #e44b4b;
    border-radius: 100%;
    cursor: pointer;
    user-select: none;
    transition: border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.1s ease;
}

.lockIcon.unlocked {
    background-color: #6c757d;
    border: 1px solid #d4d8dc;
}


.marking-modal-inner {
    overflow: hidden;
    width: 99%;
}

::v-deep #marking-modal .modal-dialog {
    max-width: 80vw;
}

.modal-close-btn {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    background: none;
    border: none;
    font-size: 1.25rem;
    line-height: 1;
    color: #6c757d;
    cursor: pointer;
    z-index: 10;
    padding: 0.25rem 0.5rem;
}

.modal-close-btn:hover {
    color: #000;
}

.marking-modal-content {
    overflow-y: auto;
    overflow-x: auto;
}

.marking-modal-scroll-wrapper {
    min-height: 400px;
    height: 60vh;
}

/* Rows with per-user mobile columns can be wider than the modal - let them
   overflow instead of wrapping (Bootstrap's .row wraps by default), and let
   the single .marking-modal-content scrollbar above handle it, so the whole
   section (header, every row, pictures) scrolls together as one unit.
   ::v-deep is needed because MarkingAttachmentComparison renders its own
   .edit-row rows as a child component (its own scope, not this one's). */
.marking-modal-scroll-wrapper ::v-deep .edit-row {
    flex-wrap: nowrap;
}

.sort-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #343a40;
    white-space: nowrap;
}

/* Same look as markings/Index.vue's .markings-sort-select */
.mobile-view-mode-select {
    max-width: 130px;
    padding: 0 5px;
    font-size: 13px;
    color: #343a40;
    background-color: #ffffff;
    border: 1px solid #dcdfe3;
    border-radius: 6px;
    outline: none;
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    cursor: pointer;

    &:focus {
        border-color: #dc2626;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
    }

    &:hover:not(:focus) {
        border-color: #c3c7cf;
    }
}

/* Hide native checkbox */
.checkbox-label input[type="checkbox"] {
    display: none;
}

/* Custom grey box */
.checkbox-label .custom-checkbox {
    width: 16px;
    height: 16px;
    border: 2px solid #ccc; /* grey box */
    display: inline-block;
    margin-left: 8px; /* spacing from text */
    position: relative;
    border-radius: 3px;
    background-color: #fff;
}

/* Red check when checked */
.checkbox-label input[type="checkbox"]:checked + .custom-checkbox::after {
    content: "";
    position: absolute;
    left: 3px;
    top: 0px;
    width: 5px;
    height: 10px;
    border: solid #dc2626;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.menu {
    list-style: none;
    padding: 0;
    user-select: none;
    flex-grow: 1;
}

.menu li {
    padding: 6px 10px 6px 26px;
    cursor: pointer;
    position: relative;
    font-weight: normal;
}

.menu li.active {
    font-weight: bold;
    color: #dc2626;
}

.menu li.active::before {
    content: "▶";
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    color: #dc2626;
}

.menu-disabled {
    color: #888888;
    pointer-events: none;
}

.has-changes {
    border-left: 3px solid #fbc120;
}

.menu-border-right {
    border-right: 2px solid #dc2626;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.parent {
    font-weight: 600;
}

.submenu {
    list-style: none;
    padding-left: 20px;
}

.approval-selection .approval {
    width: 35px;
    height: 25px;
    display: inline-block;
    margin-right: .3rem;
    vertical-align: middle;
}

.approval-selection .approval.active {
    border: solid 2px #dc2626;
    box-shadow: 1px 1px 5px 0px rgba(0, 0, 0, 0.75);
    background-image: url(../../../../images/tool-checkbox-checked.svg);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
}

</style>
